import 'antd/dist/antd.css';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import './App.css';
import RouteProtected from './components/blocks/RouteProtected/RouteProtected';
import AccountsPage from './components/pages/AccountsPage';
import ContentCollectionsPage from './components/pages/Content/CollectionsPage';
import CompanyPage from './components/pages/Company/CompanyPage';
import ContentCardsPage from './components/pages/Content/ContentCardsPage';
import DeviceTypesPage from './components/pages/DeviceTypesPage';
import FirmwaresGroupsPage from './components/pages/FirmwaresGroupsPage';
import FirmwaresPage from './components/pages/FirmwaresPage';
import LanguagesPage from './components/pages/LanguagesPage';
import LocationsPage from './components/pages/LocationsPage';
import LoginPage from './components/pages/LoginPage';
import { fetchUserProfile } from './features/auth/auth.state';
import CompanyAccountsPage from './components/pages/Company/CompanyAccountsPage';
import CompanyAccountPageBlock from './components/pages/Company/CompanyAccountPage';
import CmsUserPageBlock from './components/pages/CmsUserPage';
import CmsUsersPageBlock from './components/pages/CmsUsersPage';
import DeviceTypePage from './components/pages/DeviceTypePage/DeviceTypePage';
import LocationPage from './components/pages/LocationPage/LocationPage';
import LanguagePageBlock from './components/pages/LanguagePage';
import FirmwarePageBlock from './components/pages/FirmwarePage';
import CollectionPageBlock from './components/pages/Content/CollectionPage';
import CompaniesPageBlock from './components/pages/Company/CompaniesPage/CompaniesPage';
import ContentCardPageBlock from 'src/components/pages/Content/ContentCardPage/ContentCardPage';
import CompanyPackagePage from 'src/components/pages/Company/CompanyPackagePage/CompanyPackagePage';
import CompanyOfferPage from 'src/components/pages/Company/CompanyOfferPage';
import ContentTemplatesPageBlock from 'src/components/pages/Content/ContentTemplatesPage';
import ContentTemplatePageBlock from 'src/components/pages/Content/ContentTemplatePage/ContentTemplatePage';
import CompanyBannerPage from 'src/components/pages/Company/CompanyBannerPage/CompanyBannerPage';
import CompanyVideoPage from 'src/components/pages/Company/CompanyVideoPage/CompanyVideoPage';
import { RoleEnum } from 'src/features/users/users.types';
import WelcomePage from 'src/components/pages/WelcomePage';
import DeviceGroupsPage from './components/pages/DeviceGroupsPage';
import DeviceGroupPage from 'src/components/pages/DeviceGroupPage';
import DeviceGroupVideoPage from 'src/components/pages/DeviceGroupPage/DeviceGroupVideoPage/DeviceGroupVideoPage';
import DevicesPageBlock from './components/pages/DevicesPage/DevicesPage';
import DevicePageBlock from './components/pages/DevicesPage/DevicePage';
import DevicesPage from './components/pages/DevicesPage/DevicesPage';

const App = (): React.JSX.Element => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserProfile({}));
  }, [dispatch]);

  return (
    <>
      <BrowserRouter>
        <Switch>
          <Route exact path='/login' component={LoginPage} />
          {/* LOCATIONS */}
          <RouteProtected
            path='/settings/locations'
            component={LocationsPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          <RouteProtected
            path='/settings/locations/:locationCode'
            component={LocationPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* LANGUAGES */}
          <RouteProtected path='/settings/languages' component={LanguagesPage} exact />
          <RouteProtected
            path='/settings/languages/:languageCode'
            component={LanguagePageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* DEVICE TYPES */}
          <RouteProtected path='/settings/device-types' component={DeviceTypesPage} exact />
          <RouteProtected
            path='/settings/device-types/:deviceTypeName'
            component={DeviceTypePage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* DEVICE GROUPS */}
          <RouteProtected
            path='/settings/devices'
            component={DevicesPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* DEVICE GROUPS */}
          <RouteProtected
            path='/settings/groups'
            component={FirmwaresGroupsPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* CMS USERS */}
          <RouteProtected
            path='/settings/users'
            component={CmsUsersPageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          <RouteProtected
            path='/settings/users/:userId'
            component={CmsUserPageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* FIRMWARES */}
          <RouteProtected
            path='/settings/firmwares'
            component={FirmwaresPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          <RouteProtected
            path='/settings/firmwares/:firmwareId'
            component={FirmwarePageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          <RouteProtected
            path='/settings/devices'
            component={DevicesPageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          <RouteProtected
            path='/settings/devices/:deviceId'
            component={DevicePageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* COLLECTIONS */}
          <RouteProtected
            path='/content/collections'
            component={ContentCollectionsPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          <RouteProtected
            path='/content/collections/:collectionId'
            component={CollectionPageBlock}
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          {/* CATEGORIES */}
          <RouteProtected
            path='/content/templates'
            component={ContentTemplatesPageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          <RouteProtected
            path='/content/templates/:templateId'
            component={ContentTemplatePageBlock}
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          {/* CONTENTS */}
          <RouteProtected
            path='/content/cards'
            component={ContentCardsPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          <RouteProtected
            path='/content/cards/:contentId'
            component={ContentCardPageBlock}
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          {/* COMPANIES */}
          <RouteProtected
            path='/commercial/companies'
            component={CompaniesPageBlock}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
          />
          <RouteProtected
            path='/commercial/companies/:companyId/packages/:packageId'
            component={CompanyPackagePage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
          />
          <RouteProtected
            path='/commercial/companies/:companyId/banners/:bannerId'
            component={CompanyBannerPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
          />
          <RouteProtected
            path='/commercial/companies/:companyId/videos/:videoId'
            component={CompanyVideoPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
          />
          <RouteProtected
            exact
            path='/commercial/companies/:companyId/offers/:langCode'
            component={CompanyOfferPage}
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/*<RouteProtected path='/commercial/companies/:companyId/packages' component={CompanyPackagePage} />*/}
          <RouteProtected
            path='/commercial/companies/:companyId'
            component={CompanyPage}
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
          />

          {/* COMMERCIAL ACCOUNTS */}
          <RouteProtected
            exact
            path='/commercial/accounts'
            component={CompanyAccountsPage}
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          <RouteProtected
            exact
            path='/commercial/accounts/:accountId'
            component={CompanyAccountPageBlock}
            roles={[RoleEnum.admin, RoleEnum.observer]}
          />
          {/* ACCOUNTS */}
          <RouteProtected
            path='/content/accounts'
            component={AccountsPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          <RouteProtected
            path='/testing/groups/:groupId/videos/:videoId'
            component={DeviceGroupVideoPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
          />
          <RouteProtected
            path='/testing/groups/:groupId'
            component={DeviceGroupPage}
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          <RouteProtected
            path='/testing/groups'
            component={DeviceGroupsPage}
            exact
            roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
          />
          <RouteProtected exact path='*' component={WelcomePage} />
        </Switch>
      </BrowserRouter>
    </>
  );
};

export default App;
